import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";

/**
 * Address search bar with autocomplete suggestions (hits /ownvestor/address-suggestions)
 * and a "Search" button that reports the chosen address back to the parent via onSearch.
 *
 * Props:
 *  - onSearch(fullAddress: string): called when the user picks a suggestion (or presses
 *    Enter / clicks Search) with a non-empty address.
 *  - onClear(): called when the user clears the search.
 */
const LeadAddressSearch = ({ onSearch, onClear }) => {
  const [query, setQuery] = useState("");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [activeSearch, setActiveSearch] = useState("");

  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // fetch suggestions (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query || query.trim().length < 4 || query === selectedAddress) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setLoadingSuggestions(true);
        const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/ownvestor/address-suggestions`, {
          params: { q: query.trim() },
        });
        if (resp.data?.success) {
          setSuggestions(resp.data.addresses || []);
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error("Failed to fetch address suggestions", err);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current); // eslint-disable-next-line
  }, [query]);

  // close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectSuggestion = (s) => {
    setQuery(s.full_address);
    setSelectedAddress(s.full_address);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSearch = () => {
    const addressToSearch = (selectedAddress || query).trim();
    // if (!addressToSearch) return;
    setActiveSearch(addressToSearch);
    setShowSuggestions(false);
    onSearch?.(addressToSearch);
  };

  const handleClearSearch = () => {
    setQuery("");
    setSelectedAddress(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveSearch("");
    onClear?.();
  };

  return (
    <div className="w-full !mb-4" ref={wrapperRef}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            className="input_field focus:!outline-none w-full"
            placeholder="Search leads by address..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedAddress(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          {loadingSuggestions && <Loader2 size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}

          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute z-10 w-full !mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((s, idx) => (
                <li key={idx} className="!px-3 !py-2 cursor-pointer hover:bg-gray-100 text-sm truncate" onClick={() => handleSelectSuggestion(s)}>
                  {s.full_address}
                </li>
              ))}
            </ul>
          )}
        </div>

        <button type="button" onClick={handleSearch} className={`flex items-center justify-center gap-1 ${activeSearch || query ? "!px-4" : "!px-10"} h-10 text-white rounded cursor-pointer bg-[#008080] hover:bg-[#009999]`} title="Search leads for this address">
          <Search size={18} />
          Search
        </button>

        {(activeSearch || query) && (
          <button type="button" onClick={handleClearSearch} className="flex items-center justify-center !px-3 h-10 text-white rounded cursor-pointer bg-gray-500 hover:bg-gray-600" title="Clear search">
            <X size={18} />
          </button>
        )}
      </div>

      {activeSearch && (
        <div className="text-sm text-gray-600 !mt-2">
          Showing leads matching: <span className="font-semibold text-gray-900">{activeSearch}</span>
        </div>
      )}
    </div>
  );
};

export default LeadAddressSearch;
